import { FC, useState, useEffect } from 'react'

import useOrderReviewStore from './orderReviewStore'
import { Button, H3 } from '@/common/ui'
import { useCancelOrder } from '@/trading/tradingQueries'
import Money from '@/common/models/money'
import { tradeEstimatedBrokerage } from '@/common/utils/utils'
import { OrderExpiry, orderStatus } from '@/common/types'
import { ORDER_EXPIRY, ORDER_EXPIRY_MAPPING, ORDER_FAILURE_CODES } from '@/common/constants'
import { Dialog, DialogContent, DialogFooter } from '@/common/ui/dialogMobileSupport'
import { toast } from 'sonner'
import { Gift, AlertCircle } from 'lucide-react'
import { OrderRewardItem } from '@/trading/orderRewardItem'
import { ORDER_REVIEW_COPY, REWARDS_COPY } from '@/locales/en'
import CancelUnfilledModal from './cancelUnfilledModal'
import { Alert, AlertTitle, AlertDescription } from '@/common/ui/alert'
import { InfoIconTooltip } from '@/common/ui/tooltip'

type OrderReviewItemProps = {
    label: string
    value: string | number | React.ReactNode
}
const Item: FC<OrderReviewItemProps> = ({ label, value }) => {
    return (
        <li className="flex justify-between py-3 border-b">
            <span>{label}</span>
            <strong className="text-right ml-2">{value}</strong>
        </li>
    )
}

type Props = {
    open: boolean
    onClose: () => void
    onCancelOrder: () => void
}

export const ReviewOrderModal: FC<Props> = ({ open, onClose, onCancelOrder }) => {
    const [openCancelUnfilled, setOpenCancelUnfilled] = useState<boolean>(false)
    const { currentOrder } = useOrderReviewStore()
    const cancelOrder = useCancelOrder()
    const [cancelingOrder, setCancelingOrder] = useState<boolean>(false)

    useEffect(() => {
        if (!open) setOpenCancelUnfilled(false)
    }, [open])

    if (!currentOrder) return null

    const handleCancelOrder = async () => {
        setCancelingOrder(true)
        cancelOrder.mutate(currentOrder.orderId, {
            onSuccess: () => {
                setOpenCancelUnfilled(false)
                onCancelOrder()
            },
            onError: () => {
                toast.error('Failed to cancel order, Please contact support')
            },
            onSettled: () => {
                setCancelingOrder(false)
            },
        })
    }

    const formatExpiryDate = (date: string) => (date ? `${new Date(date).toLocaleDateString()}` : 'NA')

    const renderFailureAlert = () => {
        const failureInfo = ORDER_FAILURE_CODES[currentOrder!.failureCode as keyof typeof ORDER_FAILURE_CODES]
        if (!failureInfo) return null
        return (
            <Alert className="∂rounded-lg outline-border-outline">
                <div className="flex-1 flex justify-start items-start gap-6">
                    <AlertCircle className="h-5 w-5 text-sentiment-bad inline-block" />
                    <div className="flex-1 inline-flex flex-col justify-center items-start gap-2">
                        <AlertTitle className="text-sentiment-bad">{failureInfo.title}</AlertTitle>
                        <AlertDescription className="text-sentiment-bad">{failureInfo.description}</AlertDescription>
                    </div>
                </div>
            </Alert>
        )
    }

    const isExpiryRelatedFailure = (failureCode?: string) => {
        if (!failureCode) return false
        const expiryFailureCodes = ['INVALID_EXPIRY_DATE', 'INVALID_TIME_VALIDITY', 'ORDER_EXPIRED']
        return expiryFailureCodes.includes(failureCode)
    }

    const renderExpiryDateWithTooltip = (orderValidity: string) => {
        const hasExpiryFailure = isExpiryRelatedFailure(currentOrder.failureCode)
        if (!hasExpiryFailure) return orderValidity

        const failureInfo = ORDER_FAILURE_CODES[currentOrder.failureCode as keyof typeof ORDER_FAILURE_CODES]
        if (!failureInfo) return orderValidity

        return (
            <div className="flex items-center gap-2">
                <span>{orderValidity}</span>
                <InfoIconTooltip size={14}>
                    <p className="font-bold mb-2">{failureInfo.title}</p>
                    <p className="text-sm">{failureInfo.description}</p>
                </InfoIconTooltip>
            </div>
        )
    }

    const {
        companyName,
        expiresOn,
        orderSide,
        orderType,
        units,
        securityCode,
        validity,
        status,
        orderCompletionPrice,
        orderCompletionUnits,
        orderId,
        requestedPrice,
    } = currentOrder
    const relevantPrice = orderCompletionPrice ? orderCompletionPrice : requestedPrice
    const sharesAmount = Money.of(relevantPrice).multiply(units)
    const fee = sharesAmount ? tradeEstimatedBrokerage(sharesAmount) : Money.of(0)
    const freeTrade = currentOrder.promotion?.type === 'FREE_TRADE_REBATE'
    const orderPending = [orderStatus['IN MARKET'], orderStatus.REQUESTED].includes(status)
    const orderFailedOrCancelled = [orderStatus.FAILED, orderStatus.CANCELLED].includes(status)
    const partiallyFilled = status === orderStatus['PARTIALLY FILLED']
    const orderValidity =
        validity == 'Till Date'
            ? formatExpiryDate(expiresOn)
            : ORDER_EXPIRY[ORDER_EXPIRY_MAPPING[validity] as OrderExpiry]
    const estimatedPrefix = !orderCompletionPrice || partiallyFilled ? `${ORDER_REVIEW_COPY.estimated} ` : ''

    return (
        <>
            <Dialog open={open}>
                <DialogContent
                    onClose={onClose}
                    className="pt-16 sm:pt-0 sm:max-h-[90vh] sm:min-h-[50vh] sm:w-[94vw] sm:max-w-[680px]"
                >
                    <H3 className="text-center">
                        {ORDER_REVIEW_COPY.review} <span className="capitalize">{orderSide.toLowerCase()}</span>{' '}
                        {ORDER_REVIEW_COPY.order}
                    </H3>
                    {status === orderStatus.FAILED && currentOrder.failureCode ? renderFailureAlert() : null}
                    <ul>
                        <Item label={ORDER_REVIEW_COPY.orderReference} value={orderId} />
                        <Item label={ORDER_REVIEW_COPY.orderStatus} value={status} />
                        {/* Workaround until we change company name -> instrument name */}
                        <Item
                            label={ORDER_REVIEW_COPY.security}
                            value={companyName ? `${companyName} (${securityCode})` : securityCode}
                        />
                        <Item
                            label={ORDER_REVIEW_COPY.orderType}
                            value={orderType == 'MARKET_TO_LIMIT' ? 'Market' : 'Limit'}
                        />
                        <Item label={ORDER_REVIEW_COPY.expiryDate} value={renderExpiryDateWithTooltip(orderValidity)} />
                        {partiallyFilled ? (
                            <>
                                <Item label={ORDER_REVIEW_COPY.filled} value={orderCompletionUnits ?? 0} />
                                <Item label={ORDER_REVIEW_COPY.unfilled} value={units - (orderCompletionUnits ?? 0)} />
                                <Item label={ORDER_REVIEW_COPY.totalShares} value={units} />
                            </>
                        ) : (
                            <Item label={ORDER_REVIEW_COPY.shares} value={units} />
                        )}
                        {requestedPrice && <Item label={ORDER_REVIEW_COPY.limitPrice} value={requestedPrice} />}
                        {orderCompletionPrice && (
                            <Item label={ORDER_REVIEW_COPY.averageCompletionPrice} value={orderCompletionPrice} />
                        )}
                        {!orderFailedOrCancelled && sharesAmount.greaterThan(Money.of(0)) ? (
                            <>
                                <Item
                                    label={`${estimatedPrefix}${ORDER_REVIEW_COPY.amount}`}
                                    value={`${sharesAmount}`}
                                />
                                <Item label={`${estimatedPrefix}${ORDER_REVIEW_COPY.brokerage}`} value={`${fee}`} />
                                {freeTrade ? (
                                    <OrderRewardItem
                                        icon={<Gift size={20} />}
                                        title={REWARDS_COPY.freeTradeItem.title}
                                        description={
                                            orderCompletionPrice
                                                ? REWARDS_COPY.freeTradeItem.completedDescription
                                                : REWARDS_COPY.freeTradeItem.description
                                        }
                                    />
                                ) : null}
                                <Item
                                    label={`${estimatedPrefix}${ORDER_REVIEW_COPY.total}`}
                                    value={`${orderSide == 'BUY' ? sharesAmount.add(fee) : sharesAmount.subtract(fee)}`}
                                />
                            </>
                        ) : null}
                    </ul>
                    <DialogFooter className="gap-3">
                        {(orderPending || partiallyFilled) && (
                            <Button
                                variant="destructive"
                                size="lg"
                                className="w-full sm:w-max sm:m-auto"
                                onClick={partiallyFilled ? () => setOpenCancelUnfilled(true) : handleCancelOrder}
                                loading={cancelingOrder}
                            >
                                {partiallyFilled
                                    ? ORDER_REVIEW_COPY.cancelUnfilledOrder
                                    : ORDER_REVIEW_COPY.cancelOrder}
                            </Button>
                        )}
                        <Button variant="outline" size="lg" className="w-full sm:hidden" onClick={onClose}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <CancelUnfilledModal
                open={openCancelUnfilled}
                cancelingOrder={cancelingOrder}
                onClose={() => setOpenCancelUnfilled(false)}
                onSubmit={handleCancelOrder}
            />
        </>
    )
}
